/**
 * "How shift planning works": the four steps of the workflow and what each shift state
 * means, opened from the status strip (#2038 UX pass). Written for a planner who opens
 * the page for the first time, without a handbook.
 */
import { useLingui } from '@lingui/react';
import { Link } from 'react-router';
import { ArrowLeftRight, CheckCircle2, CircleDashed, HelpCircle, Lock, PenLine, Send, Undo2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ShiftStatus } from '@/types/shifts';
import { shiftBlockClasses } from './shiftLifecycle';

export function PlannerHelpPopover() {
  const { i18n } = useLingui();
  const swatch = 'mt-0.5 inline-block h-3 w-5 shrink-0 rounded-sm border';
  const steps = [
    i18n._({
      id: 'shifts.help.step1',
      message:
        'Add shifts: click a free cell or use Add. New shifts are drafts, which only planners see.',
    }),
    i18n._({
      id: 'shifts.help.step2',
      message:
        'Check the cover: the mark next to each group shows whether enough staff are planned for the children booked that day. The Day view shows it per quarter hour.',
    }),
    i18n._({
      id: 'shifts.help.step3',
      message:
        'Publish the period: employees then see their shifts in the app and get one notification each.',
    }),
    i18n._({
      id: 'shifts.help.step4',
      message:
        'Employees confirm in the app. If you change a published shift, the employee is notified and confirms again.',
    }),
  ];
  const states = [
    {
      key: 'draft',
      swatch: shiftBlockClasses(ShiftStatus.DRAFT),
      label: i18n._({ id: 'shifts.status.draft', message: 'Entwurf' }),
      text: i18n._({ id: 'shifts.help.state.draft', message: 'Only planners see it.' }),
    },
    {
      key: 'published',
      swatch: shiftBlockClasses(ShiftStatus.PUBLISHED),
      label: i18n._({ id: 'shifts.status.published', message: 'Veröffentlicht' }),
      text: i18n._({
        id: 'shifts.help.state.published',
        message: 'The employee sees it in the app and was notified.',
      }),
    },
    {
      key: 'confirmed',
      icon: CheckCircle2,
      label: i18n._({ id: 'shifts.status.confirmed', message: 'Bestätigt' }),
      text: i18n._({
        id: 'shifts.help.state.confirmed',
        message: 'The employee accepted the shift in the app.',
      }),
    },
    {
      key: 'changed',
      icon: PenLine,
      label: i18n._({ id: 'shifts.lifecycle.changed', message: 'Changed since publication' }),
      text: i18n._({
        id: 'shifts.help.state.changed',
        message: 'Changed after it was published; the employee was notified.',
      }),
    },
    {
      key: 'cancelled',
      swatch: shiftBlockClasses(ShiftStatus.CANCELLED),
      label: i18n._({ id: 'shifts.status.cancelled', message: 'Storniert' }),
      text: i18n._({
        id: 'shifts.help.state.cancelled',
        message: 'Cancelled, for example by an approved absence. The employee was notified.',
      }),
    },
    {
      key: 'open',
      icon: CircleDashed,
      label: i18n._({ id: 'shifts.open.label', message: 'Open' }),
      text: i18n._({
        id: 'shifts.help.state.open',
        message:
          'Nobody assigned. Once published, the employees of its groups ask for it in the app; the number shows how many asked.',
      }),
    },
    {
      key: 'swap',
      icon: ArrowLeftRight,
      label: i18n._({ id: 'shifts.swap.legend', message: 'Swap offered' }),
      text: i18n._({
        id: 'shifts.help.state.swap',
        message: 'The employee offers the shift to a colleague. Swaps close 24 hours before the start.',
      }),
    },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-muted-foreground"
          data-testid="planner-help"
        >
          <HelpCircle className="h-4 w-4" aria-hidden="true" />
          {i18n._({ id: 'shifts.help.button', message: 'How it works' })}
        </Button>
      </PopoverTrigger>
      {/* #2119 F9: the popover scrolls inside a short viewport (1280x720). */}
      <PopoverContent
        align="start"
        collisionPadding={16}
        className="max-h-[min(var(--radix-popover-content-available-height),calc(100dvh-4rem))] w-[26rem] max-w-[calc(100vw-2rem)] space-y-4 overflow-y-auto text-sm"
        data-testid="planner-help-content"
      >
        <div className="space-y-2">
          <h4 className="font-semibold">
            {i18n._({ id: 'shifts.help.title', message: 'How shift planning works' })}
          </h4>
          <ol className="list-decimal space-y-1.5 pl-5">
            {steps.map((step, index) => (
              <li key={step}>
                {step}
                {/* Working time warnings belong to the check step (#2042). */}
                {index === 1 && (
                  <p className="mt-1 text-muted-foreground" data-testid="planner-help-warnings">
                    {i18n._({
                      id: 'shifts.help.warnings',
                      message:
                        'The planner warns when a day or a week gets too long, when the rest between two working days is too short, and when you publish later than the lead time. Nothing is blocked: the shift is saved and published anyway.',
                    })}{' '}
                    <Link to="/settings/shift-planning" className="underline underline-offset-2 hover:text-foreground">
                      {i18n._({ id: 'shifts.help.warningsLink', message: 'Limits under Settings → Shift planning' })}
                    </Link>
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
        <div className="space-y-2">
          <h4 className="font-semibold">
            {i18n._({ id: 'shifts.help.statesTitle', message: 'What the colours mean' })}
          </h4>
          <ul className="space-y-1.5">
            {states.map((state) => (
              <li key={state.key} className="flex items-start gap-2">
                {state.icon ? (
                  <state.icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                ) : (
                  <span className={`${swatch} ${state.swatch}`} aria-hidden="true" />
                )}
                <span>
                  <span className="font-medium">{state.label}</span>
                  {': '}
                  <span className="text-muted-foreground">{state.text}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <ul className="space-y-1.5 border-t pt-3 text-muted-foreground">
          <li className="flex items-start gap-2">
            <Send className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {i18n._({
              id: 'shifts.help.publishNote',
              message:
                'Publish sends every draft of the visible period, or of the filtered group only.',
            })}
          </li>
          <li className="flex items-start gap-2">
            <Undo2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {i18n._({
              id: 'shifts.help.unpublishNote',
              message:
                'Unpublish takes published shifts back to draft. Employees no longer see them and are not notified.',
            })}
          </li>
          <li className="flex items-start gap-2" data-testid="planner-help-availability">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {i18n._({
              id: 'shifts.help.availabilityNote',
              message:
                'Employees enter their availability in the app. The plan hatches the hours they cannot work, marks blocked days with a lock and warns on a clash. Nothing is blocked: the shift saves anyway.',
            })}
          </li>
          <li className="flex items-start gap-2" data-testid="planner-help-lead-note">
            <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {i18n._({
              id: 'shifts.help.leadNoteCombine',
              message:
                'A group lead plans and publishes only the groups they lead; other groups are read-only. Admins see and change everything; combining groups and the combination suggestions are for admins only, in the By Group view.',
            })}
          </li>
          <li className="flex items-start gap-2">
            <CircleDashed className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {i18n._({
              id: 'shifts.help.openNote',
              message:
                'Open shift: choose Open shift instead of an employee. Click the block to approve one request; the employee gets the shift as confirmed, everyone else who asked is told.',
            })}
          </li>
          <li className="flex items-start gap-2">
            <ArrowLeftRight className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {i18n._({
              id: 'shifts.help.swapNote',
              message:
                'Swap: when a colleague accepted, click the block to approve or reject. Approve moves the shift; the colleague confirms it again. Changing the shift yourself ends open requests.',
            })}
          </li>
        </ul>
        <Link
          to="/guide/team.shifts#plan-a-week"
          className="block border-t pt-3 font-medium underline underline-offset-2 hover:text-foreground"
          data-testid="planner-help-guide-link"
        >
          {i18n._({ id: 'shifts.help.guideLink', message: 'Learn more in the guide' })}
        </Link>
      </PopoverContent>
    </Popover>
  );
}
